import { useMemo } from 'react';
import { Button } from '@travellegacy/nexops-design-system';
import { Calendar } from 'lucide-react';

export type PeriodPreset = '7d' | '30d' | '90d' | 'custom';

export interface PeriodRange {
  preset: PeriodPreset;
  /** ISO string. Undefined significa "usar default do backend" (últimos 7 dias). */
  from?: string;
  /** ISO string. Undefined significa "agora". */
  to?: string;
}

interface Props {
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
}

const PRESETS: { key: Exclude<PeriodPreset, 'custom'>; label: string; days: number }[] = [
  { key: '7d', label: '7 dias', days: 7 },
  { key: '30d', label: '30 dias', days: 30 },
  { key: '90d', label: '90 dias', days: 90 },
];

/**
 * Constrói um range fechado [from, to] onde `to` é o "agora" arredondado para
 * o fim do dia atual e `from` é `days` dias antes, no início do dia.
 */
export function buildPresetRange(preset: Exclude<PeriodPreset, 'custom'>): PeriodRange {
  const now = new Date();
  const to = new Date(now);
  const from = new Date(now);
  const preset_days = PRESETS.find((p) => p.key === preset)?.days ?? 7;
  from.setDate(from.getDate() - preset_days);
  return {
    preset,
    from: from.toISOString(),
    to: to.toISOString(),
  };
}

export function PeriodFilter({ value, onChange }: Props) {
  const activeLabel = useMemo(() => {
    if (value.preset === 'custom') return 'Personalizado';
    return PRESETS.find((p) => p.key === value.preset)?.label ?? '7 dias';
  }, [value.preset]);

  return (
    <div className="flex items-center gap-2">
      <div
        className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex"
        aria-label={`Período atual: ${activeLabel}`}
      >
        <Calendar className="h-3.5 w-3.5" />
        Período:
      </div>
      <div className="inline-flex overflow-hidden rounded-md border border-border">
        {PRESETS.map((p, idx) => {
          const isActive = value.preset === p.key;
          return (
            <Button
              key={p.key}
              type="button"
              variant={isActive ? 'default' : 'ghost'}
              size="sm"
              className={`h-8 rounded-none border-0 px-3 text-xs ${
                idx > 0 ? 'border-l border-border' : ''
              }`}
              onClick={() => onChange(buildPresetRange(p.key))}
              aria-pressed={isActive}
            >
              {p.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
